import { tempoApi } from '../../tempoApi';
import type {
  BoxPermissionPaginationResponse,
  BoxUsersPermissionsResponse,
  BulkUpdateBoxPermissionsRequest,
  UpdateBoxPermissionsRequest,
} from '../../types/permissions/boxPermissions';
import { buildBoxesURLSearchParams } from '../utils/searchParamBuilders';

const getAllBoxesUserPermissions = async ({
  cursor = '',
  limit,
}: {
  cursor?: string;
  limit?: number;
}) =>
  await tempoApi()
    .get(`api/v1/item-store/boxes/users-permissions${buildBoxesURLSearchParams({ cursor, limit })}`)
    .json<BoxPermissionPaginationResponse>();

const getUsersPermissionsForBoxId = async (boxId: number) => {
  return await tempoApi()
    .get(`api/v1/item-store/boxes/${boxId}/users-permissions`)
    .json<BoxUsersPermissionsResponse>();
};

const insertUsersPermissionsForBoxId = async (
  boxId: number,
  request: BulkUpdateBoxPermissionsRequest,
) => {
  return await tempoApi()
    .patch(`api/v1/item-store/boxes/${boxId}/users-permissions`, { json: request })
    .json<BoxUsersPermissionsResponse>();
};

const updateUsersPermissionsForBoxId = async (
  boxId: number,
  request: BulkUpdateBoxPermissionsRequest,
) => {
  return await tempoApi()
    .put(`api/v1/item-store/boxes/${boxId}/users-permissions`, { json: request })
    .json<BoxUsersPermissionsResponse>();
};

const updateSingleUserPermission = async (
  boxId: number,
  userId: string,
  request: UpdateBoxPermissionsRequest,
) => {
  return await tempoApi()
    .put(`api/v1/item-store/boxes/${boxId}/users-permissions/${userId}`, { json: request })
    .json<BoxUsersPermissionsResponse>();
};

const getBoxPermissionStatus = async (boxId: number) => {
  const response = await tempoApi()
    .get(`api/v1/item-store/boxes/${boxId}/permission-status`)
    .text();
  return response === '' ? null : response;
};

const updateBoxPermissionStatus = async (boxId: number, permissionStatus: string) => {
  return await tempoApi()
    .post(`api/v1/item-store/boxes/${boxId}/permission-status`, {
      body: permissionStatus,
      headers: { 'Content-Type': 'text/plain' },
    })
    .json();
};

const deleteUserPermissionForBoxId = async (boxId: number, userId: string) => {
  return await tempoApi().delete(`api/v1/item-store/boxes/${boxId}/user/${userId}/permissions`);
};

const bulkDeleteUserPermissions = async (boxId: number, userIds: string[]) => {
  return await tempoApi().post(`api/v1/item-store/boxes/${boxId}/users-permissions/bulk-delete`, {
    json: { userIds },
  });
};

export const BoxPermissionsApi = {
  getAllBoxesUserPermissions,
  getUsersPermissionsForBoxId,
  insertUsersPermissionsForBoxId,
  updateUsersPermissionsForBoxId,
  updateSingleUserPermission,
  getBoxPermissionStatus,
  updateBoxPermissionStatus,
  deleteUserPermissionForBoxId,
  bulkDeleteUserPermissions,
} as const;
